import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit, Trash2, Eye, Search, ChevronLeft, ChevronRight, FileText, Globe, FileLock, Clock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const RichTextEditor = lazy(() => import('@/components/RichTextEditor'));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 10;

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [coverFile, setCoverFile] = useState(null);
  const [form, setForm] = useState({
    title: '', content: '', excerpt: '', category: 'General',
    tags: '', status: 'draft', author: 'Gangadhar Som', scheduled_at: '',
  });
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
  }, [token, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => { setPage(1); }, [debouncedSearch, filterStatus]);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: PAGE_SIZE });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (filterStatus) params.set('status', filterStatus);
      const res = await axios.get(`${API}/admin/blog?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.items) {
        setPosts(res.data.items);
        setTotalPages(res.data.pages);
        setTotal(res.data.total);
      } else {
        setPosts(Array.isArray(res.data) ? res.data : []);
        setTotalPages(1);
        setTotal(Array.isArray(res.data) ? res.data.length : 0);
      }
    } catch {
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, filterStatus, token]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const openModal = (post = null) => {
    if (post) {
      setEditingPost(post);
      setForm({
        title: post.title || '',
        content: post.content || '',
        excerpt: post.excerpt || '',
        category: post.category || 'General',
        tags: (post.tags || []).join(', '),
        status: post.status || 'draft',
        author: post.author || 'Gangadhar Som',
        scheduled_at: post.scheduled_at ? post.scheduled_at.slice(0, 16) : '',
      });
    } else {
      setEditingPost(null);
      setForm({ title: '', content: '', excerpt: '', category: 'General', tags: '', status: 'draft', author: 'Gangadhar Som', scheduled_at: '' });
    }
    setCoverFile(null);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (form.status === 'scheduled' && !form.scheduled_at) {
      toast.error('Please set a scheduled date and time');
      return;
    }
    setSaving(true);
    const fd = new FormData();
    Object.entries(form).forEach(([key, val]) => {
      if (key === 'tags') {
        const arr = val ? val.split(',').map(t => t.trim()).filter(Boolean) : [];
        fd.append('tags', JSON.stringify(arr));
      } else if (key === 'scheduled_at') {
        if (form.status === 'scheduled' && val) {
          fd.append('scheduled_at', new Date(val).toISOString());
        }
      } else {
        fd.append(key, val);
      }
    });
    if (coverFile) fd.append('cover_image', coverFile);

    try {
      if (editingPost) {
        await axios.put(`${API}/admin/blog/${editingPost.id}`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Post updated');
      } else {
        await axios.post(`${API}/admin/blog`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Post created');
      }
      setModalOpen(false);
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await axios.delete(`${API}/admin/blog/${postId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Post deleted');
      fetchPosts();
    } catch {
      toast.error('Failed to delete post');
    }
  };

  const handleTogglePublish = async (post) => {
    const newStatus = post.status === 'published' ? 'draft' : 'published';
    const fd = new FormData();
    fd.append('status', newStatus);
    try {
      await axios.put(`${API}/admin/blog/${post.id}`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(newStatus === 'published' ? 'Post published' : 'Post unpublished');
      fetchPosts();
    } catch {
      toast.error('Failed to update status');
    }
  };

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-16 sm:pb-24" data-testid="blog-manager-page">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <button onClick={() => navigate('/admin/dashboard')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-accent mb-2 transition-colors" data-testid="back-to-dashboard">
              <ArrowLeft size={16} /> Back to Dashboard
            </button>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light tracking-tight" data-testid="blog-manager-title">
              Blog Manager
            </h1>
          </div>
          <Button onClick={() => openModal()} className="bg-accent hover:bg-accent/90" data-testid="create-post-btn">
            <Plus size={16} className="mr-1.5" /> New Post
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
            <input
              type="text" placeholder="Search posts..." value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg font-sans text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              data-testid="blog-search-input"
            />
          </div>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 bg-card border border-border rounded-lg font-sans text-sm" data-testid="blog-filter-status">
            <option value="">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
          </select>
          <span className="text-xs text-muted-foreground font-sans">{total} post{total !== 1 ? 's' : ''}</span>
        </div>

        {/* Posts List */}
        {loading ? (
          <p className="text-center text-muted-foreground py-12 font-sans">Loading posts...</p>
        ) : posts.length === 0 ? (
          <div className="text-center py-20" data-testid="no-posts-message">
            <FileText size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="font-serif text-2xl font-light mb-2">No blog posts yet</p>
            <p className="text-muted-foreground font-sans text-sm mb-4">Create your first article to get started.</p>
            <Button onClick={() => openModal()} className="bg-accent hover:bg-accent/90">
              <Plus size={16} className="mr-1.5" /> Create Post
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map((post) => (
              <div key={post.id} className="bg-card border border-border rounded-lg p-4 sm:p-5 flex flex-wrap items-center gap-4" data-testid={`blog-row-${post.id}`}>
                {/* Cover thumbnail */}
                <div className="w-16 h-16 flex-shrink-0 rounded bg-muted overflow-hidden">
                  {post.cover_image ? (
                    <img src={post.cover_thumb || post.cover_image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      <FileText size={20} />
                    </div>
                  )}
                </div>
                {/* Info */}
                <div className="flex-1 min-w-[200px]">
                  <h3 className="font-serif text-lg font-light line-clamp-1">{post.title}</h3>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground font-sans mt-1">
                    <span>{post.category}</span>
                    <span>{formatDate(post.created_at)}</span>
                    <span className="flex items-center gap-1">
                      {post.status === 'published' ? <Globe size={10} className="text-green-600" /> : post.status === 'scheduled' ? <Clock size={10} className="text-blue-600" /> : <FileLock size={10} className="text-amber-600" />}
                      {post.status}{post.status === 'scheduled' && post.scheduled_at ? ` (${formatDate(post.scheduled_at)})` : ''}
                    </span>
                  </div>
                </div>
                {/* Actions */}
                <div className="flex gap-2 flex-shrink-0">
                  <Button variant="outline" size="sm" onClick={() => handleTogglePublish(post)} data-testid={`toggle-publish-${post.id}`}>
                    {post.status === 'published' ? 'Unpublish' : 'Publish'}
                  </Button>
                  {post.status === 'published' && (
                    <Button variant="outline" size="sm" onClick={() => window.open(`/blog/${post.slug}`, '_blank')} data-testid={`preview-post-${post.id}`}>
                      <Eye size={14} className="mr-1" /> View
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => openModal(post)} data-testid={`edit-post-${post.id}`}>
                    <Edit size={14} className="mr-1" /> Edit
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(post.id)} data-testid={`delete-post-${post.id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8" data-testid="blog-pagination">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
              <ChevronLeft size={16} />
            </Button>
            <span className="text-sm font-sans text-muted-foreground">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
              <ChevronRight size={16} />
            </Button>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="blog-post-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">
              {editingPost ? 'Edit Post' : 'New Blog Post'}
            </DialogTitle>
            <DialogDescription className="sr-only">Blog post form</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div>
              <Label htmlFor="blog-title">Title *</Label>
              <Input id="blog-title" value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))} data-testid="blog-title-input" />
            </div>
            <div>
              <Label htmlFor="blog-excerpt">Excerpt</Label>
              <Textarea id="blog-excerpt" value={form.excerpt} onChange={(e) => setForm(p => ({ ...p, excerpt: e.target.value }))} rows={2} placeholder="Short summary for the listing page" data-testid="blog-excerpt-input" />
            </div>
            <div>
              <Label htmlFor="blog-content">Content</Label>
              <Suspense fallback={<div className="h-[300px] bg-muted/30 rounded-lg animate-pulse" />}>
                <RichTextEditor
                  content={form.content}
                  onChange={(html) => setForm(p => ({ ...p, content: html }))}
                  placeholder="Write your article content here..."
                />
              </Suspense>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="blog-category">Category</Label>
                <Input id="blog-category" value={form.category} onChange={(e) => setForm(p => ({ ...p, category: e.target.value }))} placeholder="General" data-testid="blog-category-input" />
              </div>
              <div>
                <Label htmlFor="blog-tags">Tags</Label>
                <Input id="blog-tags" value={form.tags} onChange={(e) => setForm(p => ({ ...p, tags: e.target.value }))} placeholder="art, exhibition, technique" data-testid="blog-tags-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="blog-author">Author</Label>
                <Input id="blog-author" value={form.author} onChange={(e) => setForm(p => ({ ...p, author: e.target.value }))} data-testid="blog-author-input" />
              </div>
              <div>
                <Label htmlFor="blog-status">Status</Label>
                <select id="blog-status" value={form.status} onChange={(e) => setForm(p => ({ ...p, status: e.target.value }))}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg font-sans text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                  data-testid="blog-status-select"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="scheduled">Scheduled</option>
                </select>
              </div>
            </div>
            {form.status === 'scheduled' && (
              <div>
                <Label htmlFor="blog-scheduled-at">Scheduled Publish Date & Time</Label>
                <Input
                  id="blog-scheduled-at"
                  type="datetime-local"
                  value={form.scheduled_at}
                  onChange={(e) => setForm(p => ({ ...p, scheduled_at: e.target.value }))}
                  data-testid="blog-scheduled-at-input"
                  min={new Date().toISOString().slice(0, 16)}
                />
                <p className="text-xs text-muted-foreground mt-1">The post will be automatically published at this time.</p>
              </div>
            )}
            <div>
              <Label htmlFor="blog-cover">Cover Image</Label>
              <Input id="blog-cover" type="file" accept="image/*" onChange={(e) => setCoverFile(e.target.files[0])} data-testid="blog-cover-input" />
              {editingPost?.cover_image && !coverFile && (
                <p className="text-xs text-muted-foreground mt-1">Current cover image is set. Upload new to replace.</p>
              )}
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setModalOpen(false)} data-testid="blog-cancel-btn">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-accent hover:bg-accent/90" data-testid="blog-save-btn">
                {saving ? 'Saving...' : (editingPost ? 'Update Post' : 'Create Post')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogManager;
